<script>
import Swal from 'sweetalert2';
import visa from '@/assets/images/icons/payments/img/visa-light.png';
import americanEx from '@/assets/images/icons/payments/img/american-express.png';
import masterCard from '@/assets/images/icons/payments/img/mastercard.png';
import { mapGetters } from 'vuex';
import { FPUtils } from '@/utils/FloatingPointUtils';
import { FormUtils } from '../../utils/FormUtils';
import CurrencyFormatter from '../../models/CurrencyFormatter';

export default {
  name: 'pos-step-payment',
  data: () => ({
    paymentMethodTab: 'cash',
    selectedPayment: 1,
    moneyImport: 0,
    cardType: 0,
    cardBrand: null,
    customCardBrand: '',
    cardLastNumbers: null,
    confirmationNumber: null,
    bankName: null,
    transactionChange: 0,
    paymentCardBrands: [
      {
        src: visa,
        value: 'visa',
      },
      {
        src: masterCard,
        value: 'mastercard',
      },
      {
        src: americanEx,
        value: 'american-express',
      },
    ],
    isProcessingPayment: false,
  }),
  computed: {
    ...mapGetters({
      cartTotal: 'cartTotal',
      totalOrderPaid: 'totalOrderPaid',
      realPaidCash: 'realPaidCash',
    }),
    remainingTotal() {
      const orderTotal = FPUtils.round2dec(Number(this.cartTotal.total || 0));
      const paidTotal = FPUtils.round2dec(Number(this.totalOrderPaid || 0));
      return FPUtils.round2dec(orderTotal - paidTotal);
    },
    disableMakeTransactionButton() {
      const orderTotal = FPUtils.round2dec(Number(this.cartTotal.total || 0));
      const paidTotal = FPUtils.round2dec(Number(this.totalOrderPaid || 0));
      return paidTotal >= orderTotal || this.isProcessingPayment;
    },
  },
  watch: {
    paymentMethodTab(newVal, oldVal) {
      if (oldVal !== newVal) {
        this.clearPayment();
      }
      switch (newVal) {
        case 'cash':
          this.selectedPayment = 1;
          break;
        case 'card':
          this.selectedPayment = 1.5;
          break;
        case 'transfer':
          this.selectedPayment = 5;
          break;
        default:
          this.selectedPayment = 0;
      }
    },
  },
  methods: {
    formatCurrency(value) {
      return CurrencyFormatter.format(value);
    },
    toSafeNumber(value) {
      return FormUtils.toSafeNumber(value);
    },
    onlyDigits(value) {
      return String(value || '').replace(/\D/g, '');
    },
    preventNegativeCurrencyInput(e) {
      return FormUtils.preventNegativeCurrencyInput(e);
    },
    allowOnlyNumericInput(e) {
      return FormUtils.allowOnlyNumericInput(e);
    },
    trimMax(value, max = 100) {
      return FormUtils.trimMax(value, max);
    },
    validateAmount(rawAmount, maxLen = 100) {
      const raw = String(rawAmount ?? '').trim();
      if (!raw) return 'Ingresa el importe a cobrar.';
      if (raw.length > maxLen) return `El importe no puede exceder ${maxLen} caracteres`;
      const amount = this.toSafeNumber(rawAmount);
      if (amount <= 0) return 'El importe a cobrar debe ser mayor a $0.00.';
      return true;
    },
    async getChange() {
      const total = this.remainingTotal > 0 ? this.remainingTotal : 0;
      let change = FPUtils.round2dec(FPUtils.round2dec(this.moneyImport || 0) - total);
      if (change < 0) change = 0;
      this.transactionChange = CurrencyFormatter.format(change);
      this.change = change;
    },
    validateCurrentPaymentInput(paymentMethod, amount) {
      const amountValidation = this.validateAmount(amount);
      if (amountValidation !== true) return amountValidation;
      if (paymentMethod !== 1 && amount > this.toSafeNumber(this.remainingTotal)) {
        return 'El importe no puede ser mayor al total restante en métodos distintos a efectivo';
      }
      if (paymentMethod === 2 || paymentMethod === 3) {
        const cardLastDigits = this.onlyDigits(this.cardLastNumbers);
        if (cardLastDigits.length !== 4) return 'Los últimos 4 dígitos de la tarjeta son obligatorios';
        const conf = this.onlyDigits(this.confirmationNumber);
        if (conf.length < 1 || conf.length > 100) return 'Número de confirmación inválido';
        if (!this.cardBrand) return 'Selecciona una marca de tarjeta';
        if (this.cardBrand === 'other' && !this.trimMax(this.customCardBrand, 100)) {
          return 'Especifica la marca de la tarjeta';
        }
      }
      if (paymentMethod === 4) {
        if (!this.trimMax(this.bankName, 100)) return 'Nombre del banco requerido';
        const conf = this.onlyDigits(this.confirmationNumber);
        if (conf.length < 1 || conf.length > 100) return 'Número de confirmación inválido';
      }
      if (paymentMethod === 5) {
        const conf = this.onlyDigits(this.confirmationNumber);
        if (conf.length < 1 || conf.length > 100) return 'Número de confirmación inválido';
      }
      return true;
    },
    resetPaymentState() {
      this.$store.dispatch('clearPayments');
      this.$store.dispatch('clearRealPaidCash');
      this.$store.dispatch('clearTotalOrderPaid');
      this.clearPayment();
      this.paymentMethodTab = 'cash';
      this.selectedPayment = 1;
    },
    clearPayment() {
      this.moneyImport = 0;
      this.cardType = null;
      this.transactionChange = 0;
      this.cardLastNumbers = null;
      this.cardBrand = null;
      this.customCardBrand = '';
      this.confirmationNumber = null;
      this.bankName = null;
    },
    async makeTransaction() {
      if (this.isProcessingPayment) return;
      this.isProcessingPayment = true;

      try {
        let paymentMethod = 0;
        const amount = this.toSafeNumber(this.moneyImport);
        if (this.selectedPayment === 1) {
          const validation = this.validateCurrentPaymentInput(1, amount);
          if (validation !== true) {
            Swal.fire('Error', validation, 'error');
            return;
          }
          this.$store.dispatch('setRealPaidCash', this.realPaidCash + amount);
          this.$store.dispatch('updateTotalOrderPaid', amount);
          this.clearPayment();
          return;
        }
        if (this.selectedPayment === 1.5) {
          if (!this.cardType || ![2, 3].includes(Number(this.cardType))) {
            Swal.fire('Error', 'Selecciona un tipo de tarjeta antes de continuar', 'error');
            return;
          }
          paymentMethod = Number(this.cardType);
        } else {
          paymentMethod = Number(this.selectedPayment);
        }
        const validation = this.validateCurrentPaymentInput(paymentMethod, amount);
        if (validation !== true) {
          Swal.fire('Error', validation, 'error');
          return;
        }
        const newPayment = {
          totalImport: amount,
          paymentMethod,
          confirmationNumber: this.trimMax(this.confirmationNumber, 100) || null,
          cardLastDigits: this.onlyDigits(this.cardLastNumbers).slice(0, 4) || null,
          cardBrand: this.cardBrand === 'other'
            ? this.trimMax(this.customCardBrand, 100)
            : this.trimMax(this.cardBrand, 100),
          bankName: this.trimMax(this.bankName, 100) || null,
        };
        this.$store.dispatch('addPayment', newPayment);
        this.$store.dispatch('updateTotalOrderPaid', amount);
        this.clearPayment();
      } finally {
        setTimeout(() => {
          this.isProcessingPayment = false;
        }, 200);
      }
    },
    sanitizeMoneyImport() {
      this.moneyImport = Math.max(0, this.toSafeNumber(this.moneyImport));
      this.getChange();
    },
    fillMoneyImportWithRemainingTotal() {
      this.moneyImport = Math.max(0, this.toSafeNumber(this.remainingTotal));
      this.getChange();
    },
  },
};
</script>

<template>
<div class="d-flex flex-column">
  <span class="text-body-1 font-weight-bold">Selecciona un método de pago:</span>
  <v-tabs
    v-model="paymentMethodTab"
    grow
    class="mb-2"
  >
    <v-tab href="#cash">Efectivo</v-tab>
    <v-tab href="#card">Tarjeta</v-tab>
    <v-tab href="#transfer">Transferencia</v-tab>
  </v-tabs>
  <v-tabs-items v-model="paymentMethodTab">
    <v-tab-item :value="'cash'">
      <div class="d-flex flex-column py-2">
        <v-currency-field
          v-model="moneyImport"
          outlined
          prepend-icon="mdi-cash"
          @keydown.native="preventNegativeCurrencyInput"
          @keypress.enter="makeTransaction"
          @input="sanitizeMoneyImport"
          color="secondary"
          label="Importe efectivo"
        ></v-currency-field>
      </div>
    </v-tab-item>
    <v-tab-item :value="'card'">
      <div class="d-flex flex-column py-2">
        <v-radio-group
          v-model="cardBrand"
          row
          class="pa-0 ma-0 radio-group-space-between"
        >
          <v-radio
            color="secondary"
            v-for="(provider, idx) in paymentCardBrands"
            :key="idx"
            :value="provider.value"
          >
            <template #label>
              <v-img :src="provider.src" max-width="80"/>
            </template>
          </v-radio>
          <v-radio color="secondary" :value="'other'">
            <template #label>
              <span class="mr-2">Otra:</span>
            </template>
          </v-radio>
        </v-radio-group>
        <v-text-field
          v-if="cardBrand === 'other'"
          v-model="customCardBrand"
          dense
          hide-details
          solo
          maxlength="50"
          counter="50"
          placeholder="Especificar"
          class="mb-4"
        />
        <v-radio-group
          v-model="cardType"
          row
          class="pa-0 ma-0 radio-group-space-between"
        >
          <v-radio color="secondary" label="Tarjeta de débito" :value="2" />
          <v-radio color="secondary" label="Tarjeta de crédito" :value="3" />
        </v-radio-group>
        <v-currency-field
          v-model="moneyImport"
          outlined
          prepend-icon="mdi-credit-card"
          @keydown.native="preventNegativeCurrencyInput"
          @keypress.enter="makeTransaction"
          @input="sanitizeMoneyImport"
          append-icon="mdi-arrow-down-bold-circle-outline"
          @click:append="fillMoneyImportWithRemainingTotal"
          color="secondary"
          label="Importe tarjeta"
        ></v-currency-field>
        <v-text-field
          v-model="cardLastNumbers"
          type="text"
          inputmode="numeric"
          maxlength="4"
          counter="4"
          @input="cardLastNumbers = onlyDigits($event || '').slice(0, 4)"
          @keydown="allowOnlyNumericInput"
          @keypress.enter="makeTransaction"
          outlined
          prepend-icon="mdi-numeric"
          class="pb-4"
          color="secondary"
          label="Últimos 4 dígitos de la tarjeta"
        />
        <v-text-field
          v-model="confirmationNumber"
          type="text"
          inputmode="numeric"
          maxlength="50"
          counter="50"
          @input="confirmationNumber = onlyDigits($event || '').slice(0, 100)"
          @keydown="allowOnlyNumericInput"
          @keypress.enter="makeTransaction"
          outlined
          prepend-icon="mdi-shield-check-outline"
          color="secondary"
          label="Número de confirmación"
        />
      </div>
    </v-tab-item>
    <v-tab-item :value="'check'">
      <div class="d-flex flex-column py-2">
        <v-currency-field v-model="moneyImport"
          @keydown.native="preventNegativeCurrencyInput"
          @keypress.enter="makeTransaction"
          outlined prepend-icon="mdi-checkbook"
          class="pb-4"
          @input="sanitizeMoneyImport"
          append-icon="mdi-arrow-down-bold-circle-outline"
          @click:append="fillMoneyImportWithRemainingTotal"
          color="secondary" label="Importe cheque">
        </v-currency-field>
        <v-text-field
          v-model="bankName"
          maxlength="50"
          counter="50"
          @keypress.enter="makeTransaction"
          outlined
          prepend-icon="mdi-bank"
          class="pb-4"
          color="secondary"
          label="Nombre del banco"
        />
        <v-text-field
          v-model="confirmationNumber"
          type="text"
          inputmode="numeric"
          maxlength="50"
          counter="50"
          @input="confirmationNumber = onlyDigits($event || '').slice(0, 100)"
          @keydown="allowOnlyNumericInput"
          @keypress.enter="makeTransaction"
          outlined
          prepend-icon="mdi-shield-check-outline"
          color="secondary"
          label="Número de confirmación"
        />
      </div>
    </v-tab-item>
    <v-tab-item :value="'transfer'">
      <div class="d-flex flex-column py-2">
        <v-currency-field
          v-model="moneyImport"
          @keydown.native="preventNegativeCurrencyInput"
          @keypress.enter="makeTransaction"
          class="pb-4"
          outlined prepend-icon="mdi-transfer-right"
          @input="sanitizeMoneyImport"
          append-icon="mdi-arrow-down-bold-circle-outline"
          @click:append="fillMoneyImportWithRemainingTotal"
          color="secondary" label="Importe transferencia">
        </v-currency-field>
        <v-text-field
          v-model="confirmationNumber"
          type="text"
          inputmode="numeric"
          maxlength="50"
          counter="50"
          @input="confirmationNumber = onlyDigits($event || '').slice(0, 100)"
          @keydown="allowOnlyNumericInput"
          @keypress.enter="makeTransaction"
          outlined
          prepend-icon="mdi-shield-check-outline"
          color="secondary"
          label="Número de confirmación"
        />
      </div>
    </v-tab-item>
  </v-tabs-items>
  <v-btn
    color="primary"
    class="my-4"
    @click="makeTransaction"
    :disabled="disableMakeTransactionButton"
  >
    <v-icon class="mr-2">mdi-plus-circle</v-icon>
    Agregar Pago
  </v-btn>
</div>
</template>
